import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CardPosIcon } from '../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check-circle.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as CardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as RfidTagIcon } from '../../../assets/icons/rfid-icon.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/user-icon.svg';
import { RfidToken } from '../../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchRfidSummaryApi } from '../../../redux/slices/RfidTokenSlice';

/**
 * RFID and Card Group stats overview metric cards component
 * @returns {React.JSX.Element} Stats overview cards
 */
function RfidStatsOverview(): React.JSX.Element {
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const { tokens, summary } = useAppSelector((state) => state.rfidTokens);
  const { cardPools } = useAppSelector((state) => state.cardGroups);

  useEffect(() => {
    dispatch(fetchRfidSummaryApi());
  }, [dispatch]);

  const activeTokensCount = tokens.filter((item: RfidToken) => item.status === 'ACTIVE').length;
  const personalTokensCount = tokens.filter(
    (item: RfidToken) => item.type === 'PERSONAL_TAP',
  ).length;
  const businessTokensCount = tokens.filter(
    (item: RfidToken) => item.type === 'BUSINESS_TAP',
  ).length;

  const totalCardsCount = summary?.totalCards?.total ?? tokens.length;
  const activeCardsCount = summary?.totalCards?.active ?? activeTokensCount;
  const deactivatedCardsCount =
    summary?.totalCards?.deactivated ?? tokens.length - activeTokensCount;
  const personalCardsCount = summary?.personalCards ?? personalTokensCount;
  const businessCardsCount = summary?.businessCards ?? businessTokensCount;
  const totalGroupsCount = summary?.cardGroups?.total ?? cardPools.length;

  return (
    <div className="rfid-stats-overview">
      {/* ── 1. Total RFID Cards ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--blue">
          <RfidTagIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('totalCards')}>
            {t('totalCards')}
          </span>
          <h3 className="rfid-stat-card__value">{totalCardsCount}</h3>
        </div>
      </div>

      {/* ── 2. Personal Cards ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--cyan">
          <UserIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('personalCards')}>
            {t('personalCards')}
          </span>
          <h3 className="rfid-stat-card__value">{personalCardsCount}</h3>
        </div>
      </div>

      {/* ── 3. Business Cards ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--purple">
          <CardPosIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('businessCards')}>
            {t('businessCards')}
          </span>
          <h3 className="rfid-stat-card__value">{businessCardsCount}</h3>
        </div>
      </div>

      {/* ── 4. Card Groups ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--indigo">
          <CardIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('cardGroups')}>
            {t('cardGroups')}
          </span>
          <h3 className="rfid-stat-card__value">{totalGroupsCount}</h3>
        </div>
      </div>

      {/* ── 5. Active Cards ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--green">
          <CheckCircleIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('activeCards')}>
            {t('activeCards')}
          </span>
          <h3 className="rfid-stat-card__value">{activeCardsCount}</h3>
        </div>
      </div>

      {/* ── 6. Deactivated Cards ── */}
      <div className="rfid-stat-card">
        <div className="rfid-stat-card__icon rfid-stat-card__icon--red">
          <CloseCircleIcon />
        </div>
        <div className="rfid-stat-card__info">
          <span
            className="rfid-stat-card__label"
            title={t('deactivatedCards')}>
            {t('deactivatedCards')}
          </span>
          <h3 className="rfid-stat-card__value">{deactivatedCardsCount}</h3>
        </div>
      </div>
    </div>
  );
}

export default RfidStatsOverview;
